import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from '@App/container';
import classnames from 'classnames';
import PlayerControl from '@App/components/container/PlayerControlContainer';
import PlayerStatus from '@App/components/player/PlayerStatus';
import PlayerProgress from '@App/components/player/PlayerProgress';
import TrackButtonBar from '@App/components/model/TrackButtonBar';
import SoundEffectButtonBar from '@App/components/model/SoundEffectButtonBar';
import TrackVersionsButton from '@App/components/model/track/TrackVersionsButton';
import ForbiddenPlayerMessage from '@App/components/player/ForbiddenPlayerMessage';
import Button from '@App/components/ui/Button';
import Loader from '@App/components/ui/Loader';
import Highlight from '@App/model/similarSearch/Highlight';
import { SoundEffectData } from '@App/components/model/SoundEffectPropType';

interface PlaylistEntry {
  trackReference: string
  versionReference?: string | null
  highlights?: Highlight[]
  soundEffect?: SoundEffectData | null
}

interface Props {
  loading?: boolean
  mobile: boolean
  reduced: boolean
  authenticated: boolean
  trackReference: string
  versionReference?: string | null
  albumReference?: string | null
  startPosition?: number | null
  highlights?: Highlight[]
  title?: string | null
  waveform?: number[]
  duration?: number
  audioUrl?: string | null
  cover?: string | null
  nbVersions?: number
  availableForSimilarSearch?: boolean
  soundEffect?: SoundEffectData | null
  playTrack: (trackReference: string, versionReference?: string | null, startPosition?: number | null, highlights?: Highlight[]) => void
  playSoundEffect?: (soundEffect: SoundEffectData) => void
  previousTrack?: PlaylistEntry | null
  nextTrack?: PlaylistEntry | null
  onPlay?: (() => void) | null
  onPause?: (() => void) | null
  adminEditUrl?: string
  modeLoop: boolean
  hasStreamingFeature: boolean
}

interface State {
  loaded: boolean
  playing: boolean
  forbidden: boolean
  time: number
}

export default class Player extends Component<Props, State> {
  audio;

  static propTypes = {
    loading: PropTypes.bool,
    mobile: PropTypes.bool.isRequired,
    reduced: PropTypes.bool.isRequired,
    authenticated: PropTypes.bool.isRequired,
    trackReference: PropTypes.string.isRequired,
    versionReference: PropTypes.string,
    albumReference: PropTypes.string,
    startPosition: PropTypes.number,
    highlights: PropTypes.arrayOf(Highlight),
    title: PropTypes.string,
    waveform: PropTypes.array,
    duration: PropTypes.number,
    audioUrl: PropTypes.string,
    cover: PropTypes.string,
    nbVersions: PropTypes.number,
    availableForSimilarSearch: PropTypes.bool,
    soundEffect: PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      previewUrl: PropTypes.string,
      duration: PropTypes.number,
      waveformDataUrl: PropTypes.string,
      waveformImageUrl: PropTypes.string,
      artworkUrl: PropTypes.string,
    }),
    playTrack: PropTypes.func.isRequired,
    playSoundEffect: PropTypes.func,
    previousTrack: PropTypes.shape({
      trackReference: PropTypes.string.isRequired,
      versionReference: PropTypes.string,
      highlights: PropTypes.arrayOf(Highlight),
      soundEffect: PropTypes.object,
    }),
    nextTrack: PropTypes.shape({
      trackReference: PropTypes.string.isRequired,
      versionReference: PropTypes.string,
      highlights: PropTypes.arrayOf(Highlight),
      soundEffect: PropTypes.object,
    }),
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    adminEditUrl: PropTypes.string,
    modeLoop: PropTypes.bool.isRequired,
    hasStreamingFeature: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    loading: false,
    versionReference: null,
    albumReference: null,
    startPosition: null,
    title: null,
    waveform: [],
    highlights: [],
    duration: 0,
    audioUrl: null,
    cover: null,
    nbVersions: 0,
    availableForSimilarSearch: false,
    soundEffect: null,
    playSoundEffect: null,
    previousTrack: null,
    nextTrack: null,
    onPlay: null,
    onPause: null,
    modeLoop: false,
    hasStreamingFeature: false,
  };

  constructor(props: Props) {
    super(props);

    this.audio = get('audio');

    this.state = {
      loaded: false,
      playing: false,
      forbidden: false,
      time: 0,
    };

    this.onLoad = this.onLoad.bind(this);
    this.onUnload = this.onUnload.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onPause = this.onPause.bind(this);
    this.onEnd = this.onEnd.bind(this);
    this.onTime = this.onTime.bind(this);
    this.onSeek = this.onSeek.bind(this);
    this.onPrevious = this.onPrevious.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onCloseAlert = this.onCloseAlert.bind(this);
    this.onError = this.onError.bind(this);
  }

  componentDidMount() {
    this.audio.addEventListener('canplay', this.onLoad);
    this.audio.addEventListener('emptied', this.onUnload);
    this.audio.addEventListener('play', this.onPlay);
    this.audio.addEventListener('pause', this.onPause);
    this.audio.addEventListener('ended', this.onEnd);
    this.audio.addEventListener('timeupdate', this.onTime);
    // Recover from a source that never becomes playable: a native media 'error' (expired signed
    // preview URL → 403, network failure, …) or a blocked autoplay ('forbidden' is dispatched by
    // AudioApi). Without this the store stays in PLAY_STATE.LOADING forever (see onError).
    this.audio.addEventListener('error', this.onError);
    this.audio.addEventListener('forbidden', this.onError);

    // Load audio already available at mount time. In the music flow the audio url only
    // appears once GetTrack resolves (handled by componentDidUpdate), but the sound-effect
    // flow mounts a fresh Player with the signed preview url already set — and switching
    // between the two remounts the component, so the initial url must be loaded here too.
    const { audioUrl, startPosition } = this.props;

    if (audioUrl) {
      this.audio.load(audioUrl);

      if (startPosition !== null) {
        this.audio.seek(startPosition);
      }
    } else {
      // No source ready yet (a music track still resolving via GetTrack). The audio element is a
      // shared singleton: if it is still playing the *previous* source — e.g. the sound effect we
      // just switched away from — stop it so it does not keep playing under the newly mounted
      // track until the query resolves. When a source IS ready above, load() leaves an identical
      // source untouched, so a plain navigation remount (Router key={route}) never pauses playback.
      this.audio.pause();
    }
  }

  componentWillUnmount() {
    this.audio.removeEventListener('canplay', this.onLoad);
    this.audio.removeEventListener('emptied', this.onUnload);
    this.audio.removeEventListener('play', this.onPlay);
    this.audio.removeEventListener('pause', this.onPause);
    this.audio.removeEventListener('ended', this.onEnd);
    this.audio.removeEventListener('timeupdate', this.onTime);
    this.audio.removeEventListener('error', this.onError);
    this.audio.removeEventListener('forbidden', this.onError);
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { audioUrl, onPlay, onPause, startPosition } = this.props;
    const { loaded, playing } = this.state;

    // Audio file has changed
    if (audioUrl && audioUrl !== prevProps.audioUrl) {
      this.audio.load(audioUrl);
    }

    if (startPosition !== null && startPosition !== prevProps.startPosition) {
      this.audio.seek(startPosition);
    }

    // Audio start playing (loaded and playing)
    if (onPlay && (loaded !== prevState.loaded || playing !== prevState.playing) && loaded && playing) {
      onPlay();

      if (startPosition !== null) {
        this.audio.seek(startPosition);
      }
    }

    // Audio paused
    if (onPause && !playing && playing !== prevState.playing) {
      onPause();
    }
  }

  onLoad() {
    this.setState({ loaded: true, forbidden: false });
  }

  onUnload() {
    this.setState({ loaded: false });
  }

  onPlay() {
    this.setState({ playing: this.audio.isPlaying(), forbidden: false });
  }

  onPause() {
    this.setState({ playing: this.audio.isPlaying() });
  }

  onError(event?: Event) {
    // Ignore the benign MEDIA_ERR_ABORTED some browsers raise when the source is deliberately
    // swapped (music ↔ sound effect): the freshly mounted Player is loading its own source and
    // must not be forced out of its loading state.
    const mediaError = (event?.target as HTMLAudioElement | null)?.error;
    if (mediaError && mediaError.code === MediaError.MEDIA_ERR_ABORTED) {
      return;
    }

    const { onPause } = this.props;

    // The source failed to start (expired preview URL → 403, network error, blocked autoplay).
    // Clear the loading flags so the play bar / row spinner stop spinning and, crucially, move
    // the store out of PLAY_STATE.LOADING (paused action) — otherwise SoundEffect.onPlay keeps
    // bailing on `isLoading` and every further click on the row is swallowed, with no recovery
    // short of a page reload.
    this.setState({ loaded: false, playing: false });
    onPause?.();
  }

  /**
     * Play a playlist entry, routing sound effects to the dedicated action and music tracks
     * to the regular one. Music entries carry no `soundEffect` field, so their path is untouched.
     *
     * @param {{trackReference: String, versionReference: ?String, highlights: Array, soundEffect: ?Object}} entry
     */
  advance(entry: PlaylistEntry) {
    const { playTrack, playSoundEffect } = this.props;

    if (entry.soundEffect) {
      playSoundEffect?.(entry.soundEffect);

      return;
    }

    playTrack(entry.trackReference, entry.versionReference, 0, entry.highlights);
  }

  onEnd() {
    const {
      authenticated,
      duration,
      nextTrack,
      hasStreamingFeature,
      soundEffect,
    } = this.props;
    const state: { playing: boolean, forbidden?: boolean } = { playing: this.audio.isPlaying() };

    // Auto-advance: for music, only when streaming is enabled (unchanged); sound effects
    // are free previews, so they chain automatically.
    if (nextTrack && (hasStreamingFeature || nextTrack.soundEffect)) {
      this.advance(nextTrack);
    }

    // Sound-effect previews are free (no gated "rest"), so never show the forbidden message
    // for them — the metadata vs decoded-duration mismatch would false-trigger it anyway.
    if (!authenticated && !soundEffect && this.audio.getDuration() < (duration ?? 0)) {
      state.forbidden = true;
    }

    this.setState(state as Pick<State, 'playing' | 'forbidden'>);
  }

  onCloseAlert() {
    this.setState({ forbidden: false });
  }

  onTime() {
    this.setState({ time: this.audio.getTime() });
  }

  onSeek(position: number) {
    const { duration } = this.props;

    this.audio.seek((duration ?? 0) * position);
  }

  onPrevious() {
    const { previousTrack } = this.props;

    this.audio.pause();

    this.advance(previousTrack!);
  }

  onNext() {
    const { nextTrack } = this.props;

    this.audio.pause();

    this.advance(nextTrack!);
  }

  renderPlayBar() {
    const { mobile,
      reduced,
      loading,
      waveform,
      audioUrl,
      highlights,
      duration,
      versionReference,
      trackReference,
      previousTrack,
      nextTrack,
      modeLoop,
      hasStreamingFeature,
      soundEffect,
    } = this.props;
    const { time, loaded, playing } = this.state;

    if (mobile) {
      return <PlayerProgress duration={duration} time={time} />;
    }

    const onPrevious = previousTrack ? this.onPrevious : null;
    const onNext = nextTrack ? this.onNext : null;

    if (reduced) {
      return <Fragment>
        <PlayerControl
          key="control"
          loading={!loaded}
          playing={playing}
          onPlay={this.audio.togglePlay}
          onPrevious={onPrevious}
          onNext={onNext}
          modeLoop={modeLoop}
          hasStreamingFeature={hasStreamingFeature}
        />
        <PlayerProgress key="progress" duration={duration} time={time} />
      </Fragment>;
    }

    const id = versionReference || trackReference;

    return <Fragment>
      <PlayerControl
        key="control"
        loading={!loaded}
        playing={playing}
        onPlay={this.audio.togglePlay}
        onPrevious={onPrevious}
        onNext={onNext}
        modeLoop={modeLoop}
        hasStreamingFeature={hasStreamingFeature}
      />
      <PlayerStatus
        key="status"
        id={id}
        audioFileUrl={audioUrl}
        loading={loading}
        waveform={waveform}
        waveformUrl={soundEffect?.waveformDataUrl ?? null}
        waveformImageUrl={soundEffect?.waveformImageUrl ?? null}
        highlights={highlights}
        duration={duration}
        time={time}
        onSeek={this.onSeek}
      />
    </Fragment>;
  }

  renderButtonBar() {
    const { mobile, loading, soundEffect, trackReference, versionReference, albumReference, availableForSimilarSearch, adminEditUrl } = this.props;

    if (loading) {
      return <Loader />;
    }

    // Sound effects are not tracks: show the dedicated, access-gated download control rather
    // than the track action bar, which would treat the PSE id as a track reference and gate on
    // the wrong permission (canDownload instead of canAccessSoundEffects).
    if (soundEffect) {
      return <SoundEffectButtonBar mobile={mobile} sound={soundEffect} />;
    }

    return <TrackButtonBar
      mobile={mobile}
      versionReference={versionReference!}
      trackReference={trackReference}
      albumReference={albumReference!}
      availableForSimilarSearch={availableForSimilarSearch!}
      up
      adminEditUrl={adminEditUrl}
    />;
  }

  static MobileCover(props: { cover?: string | null, playing: boolean, loading: boolean, onPlay: () => void }) {
    const { cover, playing, loading, onPlay } = props;
    const className = classnames('cover', {
      'cover--loading': loading,
      'cover--pause': !loading && playing,
      'cover--play': !loading && !playing,
    });

    return <Button
      label={null}
      disabled={loading}
      className={className}
      onClick={onPlay}
      style={cover ? { backgroundImage: `url('${cover}')` } : null}
    />;
  }

  static Cover(props: { cover?: string | null }) {
    const { cover } = props;

    return <div className="cover" style={cover ? { backgroundImage: `url('${cover}')` } : undefined} />;
  }

  renderTrack() {
    const { Cover, MobileCover } = this.constructor as typeof Player;
    const { versionReference, trackReference, cover, title, nbVersions, mobile } = this.props;
    const { loaded, playing } = this.state;

    return (
      <div className="player__details">
        {mobile ? <MobileCover cover={cover} playing={playing} loading={!loaded} onPlay={this.audio.togglePlay} /> : <Cover cover={cover} />}
        <div className="info">
          <strong key="track-title" className="title">
            {title ? title : <Loader />}
          </strong>
          <span key="track-versions">
            {versionReference} - <TrackVersionsButton trackReference={trackReference} count={nbVersions} />
          </span>
        </div>
      </div>
    );

  }

  renderForbiddenMessage() {
    const { forbidden } = this.state;

    if (!forbidden) {
      return null;
    }

    return <ForbiddenPlayerMessage onClose={this.onCloseAlert} />;
  }

  render() {
    const { mobile } = this.props;

    return (
      <div className={`player ${mobile ? 'player--mobile' : ''}`}>
        {this.renderTrack()}
        {this.renderPlayBar()}
        <div className="player__actions">
          {this.renderButtonBar()}
        </div>
        {this.renderForbiddenMessage()}
      </div>
    );
  }
}
